import { Button, EmptyState } from '@hemmafixa/ui';
import { CreditCard, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useI18n } from '@/i18n/context';
import { QueryView } from '../shared/query-view';
import { useProviderBilling, useProviderProfile } from './api';

/** Jobs and proposals need a verified company; anyone else is pointed to the company profile. */
export function VerifiedOnly({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const profile = useProviderProfile();
  const f = t.provider.findJobs;
  return (
    <QueryView query={profile} loading="detail">
      {(current) =>
        current?.status === 'verified' ? (
          children
        ) : (
          <EmptyState
            icon={ShieldCheck}
            title={f.notVerifiedTitle}
            description={f.notVerifiedText}
            action={
              <Button asChild>
                <Link to="/provider/onboarding">{f.toProfile}</Link>
              </Button>
            }
          />
        )
      }
    </QueryView>
  );
}

/**
 * Find jobs needs a subscription that gives access once billing is on (P6-09); the API then shows no jobs, so this
 * explains why and points to the billing page. Job pages stay open for jobs the provider already quoted on.
 */
export function SubscribersOnly({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const billing = useProviderBilling();
  const f = t.provider.findJobs;
  return (
    <QueryView query={billing} loading="list">
      {(current) =>
        current.credits.access ? (
          children
        ) : (
          <EmptyState
            icon={CreditCard}
            title={current.subscription?.status === 'past_due' ? f.unpaidTitle : f.noPlanTitle}
            description={current.subscription?.status === 'past_due' ? f.unpaidText : f.noPlanText}
            action={
              <Button asChild>
                <Link to="/provider/billing">{f.toBilling}</Link>
              </Button>
            }
          />
        )
      }
    </QueryView>
  );
}
